// 普段のパリィの光 W 系 (2026-10-08・DEV ?parryFlash=w1|w2|w3 で試遊・既定はまだ SA0)。
//   ユーザー (10-08)：今の光の雲は「爆発してるみたい」。近いのは SEKIRO 弾き・ゼンゼロ 特殊ガード・Lies of P。
//   取り入れる要素 =「体が照らされる」「細い線の火花」。光の雲 (bloom_*) の素材は使わない。
//   見た目の正本 = 撮影台 .ai-team/checks/parry-wash-play-1008/harness/proto6.ts (WP/WCOL/CORE/SPARK/RING) と
//   harness.ts の drawWash。撮影台の照らしは画素を1つずつ読む (getImageData) ので遅い → ここでは読み戻しなしで
//   近似する (体の絵を別のキャンバスに1回描き、灰色化・色の掛け算・距離のマスクで重ねる)。
//
// 時間の数え方 (撮影台との対応)：ゲームの時計 state.time は止め (ヒットストップ) の間は進まない
//   (game.ts updateGame の `if (state.hitStopRemaining > 0)` が時刻を足さずに返す)。parry_spark の t は
//   当たった tick の時刻なので、止めの間ずっと age = 0、止めが明けた最初の tick で age = 1/60。
//   撮影台は止めの間を age = 1/60 で止めていたので「撮影台の age − 1/60 = ゲームの age」。
//   以下の関数の age はすべてゲームの age (= 止めが明けてからの経過秒、止めの間は 0)。

export type WashVariant = 'W1' | 'W2' | 'W3' | 'W2S' | 'W2X'
export type WashParams = {
  rJ: number; rN: number            // 照らす半径 (接点から・ゲーム座標)。人物の背丈 ≒ 150
  faceJ: number; faceN: number      // 体の面の明るさ
  rimJ: number; rimN: number        // 接点に面した縁の明るさ
  pow: number                       // 距離で落ちる速さ
  life: number                      // 止めが明けてから照らしが消えるまで (秒)
  bloomBlur: number; bloomA: number // 照らした体のにじみ (撮影台の値＝2倍キャンバスの px。ゲーム座標では半分)
  bleedR: number; bleedA: number    // 背景に乗る接点のにじみ (ごく弱く)
  ring: boolean
}
export const WP: Record<WashVariant, WashParams> = {
  W1: { rJ: 190, rN: 165, faceJ: 0.75, faceN: 0.7, rimJ: 1.2, rimN: 1.0, pow: 1.5, life: 0.2, bloomBlur: 5, bloomA: 0.4, bleedR: 34, bleedA: 0.1, ring: false },
  W2: { rJ: 280, rN: 250, faceJ: 1.0, faceN: 0.75, rimJ: 1.5, rimN: 1.15, pow: 1.2, life: 0.26, bloomBlur: 14, bloomA: 0.9, bleedR: 44, bleedA: 0.14, ring: false },
  W2S: { rJ: 378, rN: 338, faceJ: 1.5, faceN: 1.125, rimJ: 2.25, rimN: 1.725, pow: 1.2, life: 0.30, bloomBlur: 14, bloomA: 0.9, bleedR: 125, bleedA: 0.16, ring: false },
  W2X: { rJ: 476, rN: 425, faceJ: 2, faceN: 1.5, rimJ: 3, rimN: 2.3, pow: 1.2, life: 0.30, bloomBlur: 14, bloomA: 0.9, bleedR: 155, bleedA: 0.22, ring: false },
  W3: { rJ: 280, rN: 250, faceJ: 1.0, faceN: 0.75, rimJ: 1.5, rimN: 1.15, pow: 1.2, life: 0.26, bloomBlur: 14, bloomA: 0.9, bleedR: 44, bleedA: 0.14, ring: true },
}
/** 光の色。完璧 = 琥珀、普段 = 銀白。 */
export const WCOL = { just: [255, 172, 72], normal: [225, 233, 255] } as const
/** 面の明るさ＝ base + k × 絵の明るさ (完璧は黒い服も琥珀に・普段は黒髪/黒服を灰色に浮かせない)。 */
export const WASH_FACE_LUM = { just: { base: 0.5, k: 0.7 }, normal: { base: 0.2, k: 1.0 } } as const
/** 接点より下は距離を 1.7 倍に数える (上半身に寄せる)。 */
export const WASH_BELOW_STRETCH = 1.7

/** 照らしの時間の形：止めの間は最大、明けてから life 秒で滑らかに 0。 */
export function washEnv(age: number, life: number): number {
  if (age < 0) return 0
  const e = Math.max(0, 1 - age / life)
  return e * e * (3 - 2 * e)
}

// 接点の小さな芯 (白い芯 r7 + 縁 r17)。止めの間は出たまま、明けて 50ms で消える。
export const CORE = { r: 7, halo: 17, life: 0.05 } as const
export function coreEnv(age: number): number {
  if (age < 0) return 0
  const e = Math.max(0, 1 - age / CORE.life)
  return e * e
}

// 細い線の火花。
export const SPARK = { n: 28, fan: 0.6, fanFrom: -175, fanSpan: 150, vMin: 220, vMax: 1000, drag: 7.5, grav: 260,
  lenK: 0.04, lenMax: 44, lenMin: 3, width: 0.9, lifeMin: 0.15, lifeMax: 0.25, leadMin: 0.015, leadSpan: 0.056 } as const
/**
 * 線の火花を描いてよいか。止めの間 (age が 0 のまま) は出さない＝接点に小さな星形が固まって見えるのを防ぐ
 *   (ユーザーは星形・十字を「形すぎる」と嫌った)。止めが明けた最初のコマで一気に散る。
 */
export function lineSparksVisible(age: number): boolean { return age > 0 }

function h01(n: number): number { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }
const DEG = Math.PI / 180

/** 1本ごとの値 (テストでも使う)。lead＝出た時刻を先へずらす量 (15〜71ms)。 */
export function sparkStreak(i: number, seed = 17): { life: number; lead: number; fan: boolean; ang: number; v: number; lenMul: number; widthMul: number } {
  const life = SPARK.lifeMin + (SPARK.lifeMax - SPARK.lifeMin) * h01(seed + i * 4.7)
  const lead = SPARK.leadMin + SPARK.leadSpan * h01(seed + i * 9.7)
  const fan = h01(seed + i * 1.9) < SPARK.fan
  const ang = fan ? (SPARK.fanFrom + SPARK.fanSpan * h01(seed + i * 1.3)) * DEG : 360 * h01(seed + i * 2.3) * DEG
  const v = SPARK.vMin + (SPARK.vMax - SPARK.vMin) * Math.pow(h01(seed + i * 2.9), 1.2)
  return { life, lead, fan, ang, v, lenMul: 0.45 + 1.0 * h01(seed + i * 8.3), widthMul: 0.7 + 0.6 * h01(seed + i * 6.1) }
}

/** Round 2 only: deterministic irregular clusters, fine heads and ballistic tails. */
export const PARTICLE_SPARK = { n: 116, streaks: 5, lifeMax: 0.60, drag: 3.4, gravity: 720 } as const
export function isStrongWash(v: WashVariant | null): boolean { return v === 'W2S' || v === 'W2X' }
export function sparkParticle(i: number, seed = 17) {
  const long = i < PARTICLE_SPARK.streaks
  const cluster = Math.floor(h01(seed + i * 2.73) * 7)
  const angle = h01(seed + cluster * 31.7) * Math.PI * 2 + (h01(seed + i * 6.31) - 0.5) * 1.1
  return { angle, speed: (long ? 650 : 90) + h01(seed + i * 7.17) ** 1.7 * (long ? 450 : 730),
    life: long ? 0.18 + h01(seed + i * 8.73) * 0.15 : 0.15 + h01(seed + i * 8.73) * 0.45,
    lead: 0.008 + h01(seed + i * 5.19) * 0.027,
    size: long ? 2 : 1.2 + h01(seed + i * 3.13) ** 2 * 0.8,
    originX: Math.cos(h01(seed + i * 12.37) * Math.PI * 2) * Math.sqrt(h01(seed + i * 14.11)) * 9,
    originY: Math.sin(h01(seed + i * 12.37) * Math.PI * 2) * Math.sqrt(h01(seed + i * 14.11)) * 9,
    brightness: 0.65 + h01(seed + i * 9.11) * 0.35, long }
}
export function sparkParticlePosition(p: ReturnType<typeof sparkParticle>, t: number) {
  const k = PARTICLE_SPARK.drag, g = PARTICLE_SPARK.gravity
  const travel = (1 - Math.exp(-k * t)) / k
  return { x: p.originX + Math.cos(p.angle) * p.speed * travel,
    y: p.originY + Math.sin(p.angle) * p.speed * travel + g / k * (t - travel) }
}
/** The game removes owner FX at 0.5s. Only the new DEV variants retain the final 0.1s.
 * Caller clears on leaving play, switching modes, encounter change or rewinding time. */
export function createWashSparkRetention<T extends { kind: string; t: number }>() {
  let kept: T[] = [], last = -Infinity
  return (fx: T[], now: number, enabled: boolean): T[] => {
    if (!enabled || now < last) kept = []
    last = now
    if (!enabled) return fx
    kept = kept.filter(f => now >= f.t && now - f.t < PARTICLE_SPARK.lifeMax)
    for (const f of fx) if (f.kind === 'parry_spark' && now >= f.t && now - f.t < PARTICLE_SPARK.lifeMax && !kept.includes(f)) kept.push(f)
    return [...fx, ...kept.filter(f => !fx.includes(f))]
  }
}
function drawParticleSparks(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, just: boolean, seed: number): void {
  if (!lineSparksVisible(age)) return
  ctx.lineCap = 'butt'
  for (let i = 0; i < PARTICLE_SPARK.n; i++) {
    const p = sparkParticle(i, seed)
    if (age >= p.life) continue
    const t = age + p.lead, pos = sparkParticlePosition(p, t)
    // Tail follows the integrated curved path, with length driven by current velocity.
    const decay = Math.exp(-PARTICLE_SPARK.drag * t)
    const vx = Math.cos(p.angle) * p.speed * decay
    const vy = Math.sin(p.angle) * p.speed * decay + PARTICLE_SPARK.gravity / PARTICLE_SPARK.drag * (1 - decay)
    const speed = Math.hypot(vx, vy)
    const progress = age / p.life
    const length = Math.max(2.5, Math.min(p.long ? 48 : 38, speed * (p.long ? 0.075 : 0.055) * (1 - progress * 0.7)))
    const tailTime = Math.max(0, t - length / Math.max(1, speed))
    const prev = sparkParticlePosition(p, tailTime)
    // Full alpha at birth; visible flight, then fade to zero over the final quarter.
    const f = Math.min(1, (1 - progress) * 4)
    const flicker = 1 - Math.min(1, age / 0.04) * 0.16 * (0.5 + 0.5 * Math.sin(age * (90 + i % 43) + i * 7))
    const a = f * flicker
    const mix = (start: number[], end: number[], k: number) => start.map((v,j) => Math.round(v + (end[j]-v)*k)).join(',')
    const head = just ? mix([255,244,208], [255,138,42], progress) : mix([255,255,255], [183,211,255], progress)
    const tail = just ? mix([255,192,96], [230,91,20], progress) : mix([218,235,255], [135,180,245], progress)
    const px = x + pos.x, py = y + pos.y, tx = x + prev.x, ty = y + prev.y
    const grad = ctx.createLinearGradient(px, py, tx, ty)
    grad.addColorStop(0, `rgba(${head},${a})`); grad.addColorStop(0.25, `rgba(${tail},${a * p.brightness})`); grad.addColorStop(1, `rgba(${tail},0)`)
    // A narrow additive glow follows the same curved streak, never a disc at its head.
    const points = Array.from({ length: 5 }, (_, j) => sparkParticlePosition(p, t + (tailTime - t) * j / 4))
    ctx.strokeStyle = grad; ctx.lineWidth = p.size * 2.4
    ctx.globalAlpha = 0.32 * p.brightness
    ctx.beginPath(); ctx.moveTo(px, py)
    for (const point of points.slice(1)) ctx.lineTo(x + point.x, y + point.y)
    ctx.stroke(); ctx.globalAlpha = 1
    // Filled ribbon tapers from the bright leading edge to zero at the cool tail.
    // No circular cap: even a slow ember remains a short, narrow glint.
    const edges = points.map((point, j) => {
      const before = points[Math.max(0, j - 1)], after = points[Math.min(4, j + 1)]
      const dx = after.x - before.x, dy = after.y - before.y, d = Math.hypot(dx, dy) || 1
      const half = p.size * 0.5 * (1 - j / 4)
      return { x: x + point.x, y: y + point.y, nx: -dy / d * half, ny: dx / d * half }
    })
    ctx.fillStyle = grad; ctx.beginPath()
    ctx.moveTo(edges[0].x + edges[0].nx, edges[0].y + edges[0].ny)
    for (const e of edges.slice(1)) ctx.lineTo(e.x + e.nx, e.y + e.ny)
    for (const e of edges.slice().reverse()) ctx.lineTo(e.x - e.nx, e.y - e.ny)
    ctx.closePath(); ctx.fill()
  }
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rgb: string, a: number): void {
  if (a <= 0.003 || r <= 0.5) return
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, `rgba(${rgb},${a})`)
  g.addColorStop(0.4, `rgba(${rgb},${a * 0.45})`)
  g.addColorStop(1, `rgba(${rgb},0)`)
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}

/** 接点のにじみ・線の火花・芯 (世界座標・drawFx の parry_spark の位置で呼ぶ)。owner FX は 0.5 秒 = 火花の最長 0.25+0.071 秒より長い。 */
export function drawParryWashFx(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, just: boolean, variant: WashVariant, seed = 17): void {
  if (age < 0) return
  const P = WP[variant]
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  // 背景に乗る接点のにじみ (ごく弱い・画面は染めない)
  glow(ctx, x, y, P.bleedR, just ? '255,170,80' : '190,205,250', P.bleedA * washEnv(age, P.life))
  if (isStrongWash(variant)) drawParticleSparks(ctx, x, y, age, just, seed)
  else if (lineSparksVisible(age)) {
    ctx.lineCap = 'round'
    const head = just ? '255,246,215' : '255,255,255'
    const tail = just ? '255,160,60' : '170,195,255'
    for (let i = 0; i < SPARK.n; i++) {
      const s = sparkStreak(i, seed)
      if (age >= s.life) continue
      const tl = Math.max(0.004, age + s.lead)
      const k = SPARK.drag
      const dist = (s.v / k) * (1 - Math.exp(-k * tl))
      const px = x + Math.cos(s.ang) * dist
      const py = y + Math.sin(s.ang) * dist + 0.5 * SPARK.grav * tl * tl
      const vv = s.v * Math.exp(-k * tl)
      const vx = Math.cos(s.ang) * vv, vy = Math.sin(s.ang) * vv + SPARK.grav * tl
      const sp = Math.hypot(vx, vy) || 1
      const len = Math.max(SPARK.lenMin, Math.min(SPARK.lenMax, sp * SPARK.lenK * s.lenMul))
      const f = 1 - age / s.life
      const a = Math.min(1, f * 1.6)
      const tx = px - (vx / sp) * len, ty = py - (vy / sp) * len
      const g = ctx.createLinearGradient(px, py, tx, ty)
      g.addColorStop(0, `rgba(${head},${a})`)
      g.addColorStop(0.35, `rgba(${tail},${a * 0.8})`)
      g.addColorStop(1, `rgba(${tail},0)`)
      ctx.strokeStyle = g
      ctx.lineWidth = SPARK.width * s.widthMul * (0.6 + 0.4 * f)
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(tx, ty); ctx.stroke()
    }
  }
  const ce = coreEnv(age)
  if (ce > 0) {
    glow(ctx, x, y, CORE.halo, just ? '255,200,120' : '205,220,255', 0.85 * ce)
    glow(ctx, x, y, CORE.r, '255,255,250', ce)
  }
  ctx.restore()
}

// W3 の薄い輪：主人公の胴を囲む透けた細い輪。いびつ・1か所大きく欠ける・0.13 秒で広がって消える。
export const RING = { r0: 46, r1: 92, life: 0.13, width: 2.2, a: 0.75 } as const
export function ringRadius(age: number): number {
  const p = Math.min(1, Math.max(0, age) / RING.life)
  return RING.r0 + (RING.r1 - RING.r0) * (1 - (1 - p) * (1 - p))
}
let ringCanvas: HTMLCanvasElement | undefined
/** 世界座標 (cx,cy) を中心に描く。にじみ層は別キャンバスへ線を引いてから1回だけぼかして重ねる (線ごとの filter は重い)。 */
export function drawWashRing(ctx: CanvasRenderingContext2D, cx: number, cy: number, age: number, just: boolean, seed = 5): void {
  if (age < 0 || age >= RING.life || typeof document === 'undefined') return
  const p = age / RING.life
  const R = ringRadius(age)
  const A = RING.a * (1 - p) * (1 - p * 0.3)
  const col = just ? '255,196,100' : '205,222,255'
  const ph1 = h01(seed) * 6.28, ph2 = h01(seed + 1) * 6.28, gap = h01(seed + 2) * 6.28
  const N = 72
  ringCanvas ??= document.createElement('canvas')
  if (ringCanvas.width !== ctx.canvas.width) ringCanvas.width = ctx.canvas.width
  if (ringCanvas.height !== ctx.canvas.height) ringCanvas.height = ctx.canvas.height
  const r = ringCanvas.getContext('2d')!
  const m = ctx.getTransform(), s = Math.hypot(m.a, m.b)
  const c = m.transformPoint({ x: cx, y: cy })
  const half = (R * 1.12 * 1.08 + 16) * s
  const bx = Math.max(0, Math.floor(c.x - half)), by = Math.max(0, Math.floor(c.y - half))
  const bw = Math.min(ringCanvas.width, Math.ceil(c.x + half)) - bx, bh = Math.min(ringCanvas.height, Math.ceil(c.y + half)) - by
  if (bw <= 0 || bh <= 0) return
  const rr = (a: number) => R * (1 + 0.07 * Math.sin(3 * a + ph1) + 0.04 * Math.sin(5 * a + ph2))
  // 2 回描く：太くぼかした層 (にじみ) と細い層。撮影台のぼかし 4px / 1px は 2 倍キャンバスの値 → ゲーム座標で 2 / 0.5。
  for (const [w, blur, ka] of [[RING.width * 3.2, 2, 0.35], [RING.width, 0.5, 1]] as [number, number, number][]) {
    r.resetTransform(); r.clearRect(bx, by, bw, bh)
    r.setTransform(m); r.lineCap = 'round'; r.lineWidth = w * (1 - 0.4 * p)
    for (let i = 0; i < N; i++) {
      const a0 = (i / N) * Math.PI * 2, a1 = ((i + 1) / N) * Math.PI * 2, am = (a0 + a1) / 2
      const dg = Math.abs(Math.atan2(Math.sin(am - gap), Math.cos(am - gap)))
      const fade = Math.min(1, dg / 1.3) * (0.55 + 0.45 * Math.sin(am * 2 + ph2))
      if (fade <= 0.02) continue
      r.strokeStyle = `rgba(${col},${A * fade * ka})`
      r.beginPath()
      r.moveTo(cx + Math.cos(a0) * rr(a0), cy + Math.sin(a0) * rr(a0) * 1.08)
      r.lineTo(cx + Math.cos(a1) * rr(a1), cy + Math.sin(a1) * rr(a1) * 1.08)
      r.stroke()
    }
    ctx.save(); ctx.resetTransform(); ctx.globalCompositeOperation = 'lighter'; ctx.filter = `blur(${blur * s}px)`
    ctx.drawImage(ringCanvas, bx, by, bw, bh, bx, by, bw, bh)
    ctx.restore()
  }
}

// ── 体の照らし (読み戻しなし) ──────────────────────────────────────────────
//   撮影台：体の画素ごとに 光の色 × min(1, 落ち方 × 時間 × (縁なら rim / 面なら face × (base + k × 明るさ)))。
//   ここ：体の絵 (別キャンバスに1回描いた物) から
//     L = 灰色化 (filter grayscale) × 光の色 (multiply)            … 明るさに比例する分
//     G = 体の形を光の色×base で塗る + L × k (加算)                  … 面
//     E = 体の形 − 光から遠ざける向きにずらした体の形 → 光の色        … 接点に面した縁 (rim)
//     M = 接点からの距離で落ちるマスク (下は 1.7 倍・足元の影の高さは消す)
//   G×face と E×rim を M で切り抜き、時間の形 e を掛けて加算で重ねる (0.4 のぼかし + にじみ層)。
//   近似した所：撮影台は縁と面が排他 (縁の画素は rim だけ)、ここは面の上に (rim − face) を足す。
//     撮影台は画素ごとに min(1, k) で頭打ち、ここは色の通り道ごとに頭打ち (完璧の明るい所で少し黄寄り)。
//     足元の影は「背景を暗くしただけの画素」を除く代わりに、足元 (footY−30〜−6) でマスクを薄めて消す。
export type WashScratch = { body: HTMLCanvasElement; lum: HTMLCanvasElement; lit: HTMLCanvasElement; mask: HTMLCanvasElement }
let scratch: WashScratch | undefined
function getScratch(w: number, h: number): WashScratch {
  scratch ??= { body: document.createElement('canvas'), lum: document.createElement('canvas'), lit: document.createElement('canvas'), mask: document.createElement('canvas') }
  for (const c of Object.values(scratch)) {
    if (c.width !== w) c.width = w
    if (c.height !== h) c.height = h
  }
  return scratch
}
export function washRadius(variant: WashVariant, just: boolean, scale = 1): number {
  const P = WP[variant]
  return (just ? P.rJ : P.rN) * scale
}
/** 照らしの距離の重み (テスト用・撮影台と同じ式)。dy = 画素 y − 接点 y (下が正)。 */
export function washFalloff(dx: number, dy: number, radius: number, pow: number): number {
  const d = Math.hypot(dx, dy > 0 ? dy * WASH_BELOW_STRETCH : dy)
  return d >= radius ? 0 : Math.pow(1 - d / radius, pow)
}

/** 測定用 (DEV の撮影台だけが読む)：直近の体の照らしにかかった CPU 側の ms の合計と回数。 */
export const washCost = { ms: 0, calls: 0 }

/**
 * 体を1回だけ描き (姿勢・見え方・変換はそのまま)、その上に W 系の照らしを重ねる。
 * spark = いま効いている parry_spark。bodyX/bodyY = 体の中心 (縁の向き用)、footY = 足元 (影を照らさない)。
 */
export function drawWashLitBody(ctx: CanvasRenderingContext2D,
  spark: { x: number; y: number; t: number; isJust?: boolean; intensity?: number }, now: number, variant: WashVariant, radiusScale: number,
  bodyX: number, bodyY: number, footY: number, drawBody: (target: CanvasRenderingContext2D) => void): void {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  const W = ctx.canvas.width, H = ctx.canvas.height
  const S = getScratch(W, H)
  const b = S.body.getContext('2d')!, m = ctx.getTransform()
  b.resetTransform(); b.clearRect(0, 0, W, H); b.save(); b.setTransform(m); drawBody(b); b.restore()
  ctx.save(); ctx.resetTransform(); ctx.drawImage(S.body, 0, 0); ctx.restore()

  const P = WP[variant], just = spark.isJust ?? false
  const age = now - spark.t
  const e = washEnv(age, P.life)
  if (e <= 0.01) return
  const s = Math.hypot(m.a, m.b)
  const c = m.transformPoint({ x: spark.x, y: spark.y })
  const R = washRadius(variant, just, radiusScale) * s
  const pad = Math.ceil((P.bloomBlur / 2) * s * 2 + 4)
  const x0 = Math.max(0, Math.floor(c.x - R - pad)), y0 = Math.max(0, Math.floor(c.y - R - pad))
  const x1 = Math.min(W, Math.ceil(c.x + R + pad)), y1 = Math.min(H, Math.ceil(c.y + R / WASH_BELOW_STRETCH + pad))
  const bw = x1 - x0, bh = y1 - y0
  if (bw <= 0 || bh <= 0) return
  const col = just ? WCOL.just : WCOL.normal
  const rgb = `rgb(${col[0]},${col[1]},${col[2]})`
  const fl = just ? WASH_FACE_LUM.just : WASH_FACE_LUM.normal
  const blit = (target: CanvasRenderingContext2D, src: HTMLCanvasElement) => target.drawImage(src, x0, y0, bw, bh, x0, y0, bw, bh)

  // L = 明るさ × 光の色 (体の形で切り抜き)
  const L = S.lum.getContext('2d')!
  L.resetTransform(); L.globalCompositeOperation = 'source-over'; L.globalAlpha = 1; L.clearRect(x0, y0, bw, bh)
  L.filter = 'grayscale(1)'; blit(L, S.body); L.filter = 'none'
  L.globalCompositeOperation = 'multiply'; L.fillStyle = rgb; L.fillRect(x0, y0, bw, bh)
  L.globalCompositeOperation = 'destination-in'; blit(L, S.body)
  // G = 体の形 × 光の色 × base + L × k
  const G = S.lit.getContext('2d')!
  G.resetTransform(); G.globalCompositeOperation = 'source-over'; G.globalAlpha = 1; G.clearRect(x0, y0, bw, bh)
  blit(G, S.body)
  G.globalCompositeOperation = 'source-in'; G.fillStyle = `rgb(${col.map(v => Math.round(v * fl.base)).join(',')})`; G.fillRect(x0, y0, bw, bh)
  G.globalCompositeOperation = 'lighter'; G.globalAlpha = fl.k; blit(G, S.lum); G.globalAlpha = 1
  // E = 接点に面した縁 (L は使い終わったので縁に使い回す)。体の形から、光と反対へ 3 (ゲーム座標) ずらした形を引く。
  const d = Math.hypot(spark.x - bodyX, spark.y - bodyY) || 1
  const ux = 3 * (spark.x - bodyX) / d, uy = 3 * (spark.y - bodyY) / d
  L.globalCompositeOperation = 'source-over'; L.clearRect(x0, y0, bw, bh); blit(L, S.body)
  L.globalCompositeOperation = 'destination-out'
  L.drawImage(S.body, x0, y0, bw, bh, x0 - (m.a * ux + m.c * uy), y0 - (m.b * ux + m.d * uy), bw, bh)
  L.globalCompositeOperation = 'source-in'; L.fillStyle = rgb; L.fillRect(x0, y0, bw, bh)
  // M = 距離で落ちるマスク。上半分は円、下半分は縦を 1/1.7 に縮めた楕円 (上半身に寄せる)。
  const M = S.mask.getContext('2d')!
  M.resetTransform(); M.globalCompositeOperation = 'source-over'; M.globalAlpha = 1; M.clearRect(x0, y0, bw, bh)
  const g = M.createRadialGradient(c.x, c.y, 0, c.x, c.y, R)
  for (let i = 0; i <= 16; i++) g.addColorStop(i / 16, `rgba(255,255,255,${Math.pow(1 - i / 16, P.pow)})`)
  M.fillStyle = g
  M.save(); M.beginPath(); M.rect(x0, y0, bw, Math.max(0, c.y - y0)); M.clip(); M.fillRect(x0, y0, bw, bh); M.restore()
  M.save(); M.beginPath(); M.rect(x0, c.y, bw, Math.max(0, y1 - c.y)); M.clip()
  M.translate(c.x, c.y); M.scale(1, 1 / WASH_BELOW_STRETCH); M.translate(-c.x, -c.y)
  M.fillRect(c.x - R, c.y, 2 * R, R); M.restore()
  // 足元の影の高さでは消す (影は照らさない・足先へ向けて滑らかに)
  const fTop = m.transformPoint({ x: bodyX, y: footY - 30 }).y, fBot = m.transformPoint({ x: bodyX, y: footY - 6 }).y
  if (fBot > fTop) {
    const fg = M.createLinearGradient(0, fTop, 0, fBot)
    fg.addColorStop(0, 'rgba(255,255,255,1)'); fg.addColorStop(1, 'rgba(255,255,255,0)')
    M.globalCompositeOperation = 'destination-in'; M.fillStyle = fg; M.fillRect(x0, y0, bw, bh)
    M.globalCompositeOperation = 'source-over'
  }
  G.globalCompositeOperation = 'destination-in'; blit(G, S.mask)
  L.globalCompositeOperation = 'destination-in'; blit(L, S.mask)
  // 面×face と縁×rim を1枚にまとめる (M は使い終わったので使い回す)
  const face = just ? P.faceJ : P.faceN, rim = just ? P.rimJ : P.rimN
  M.globalCompositeOperation = 'source-over'; M.clearRect(x0, y0, bw, bh)
  for (let left = face; left > 0.001; left -= 1) { M.globalAlpha = Math.min(1, left); M.globalCompositeOperation = left === face ? 'source-over' : 'lighter'; blit(M, S.lit) }
  // 撮影台は縁の画素では面の代わりに rim を使う (足さない)。ここは面の上に縁を足すので、足すのは差の分だけ。
  M.globalCompositeOperation = 'lighter'
  for (let left = Math.max(0, rim - face); left > 0.001; left -= 1) { M.globalAlpha = Math.min(1, left); blit(M, S.lum) }
  M.globalAlpha = 1; M.globalCompositeOperation = 'source-over'
  // 重ねる：少しだけぼかした本体 + 体のまわりへ少し漏れるにじみ
  ctx.save(); ctx.resetTransform(); ctx.globalCompositeOperation = 'lighter'
  ctx.filter = `blur(${0.4 * s}px)`; ctx.globalAlpha = e; blit(ctx, S.mask)
  if (P.bloomA > 0) { ctx.filter = `blur(${(P.bloomBlur / 2) * s}px)`; ctx.globalAlpha = Math.min(1, e * P.bloomA); blit(ctx, S.mask) }
  ctx.restore()
  if (t0) { washCost.ms += performance.now() - t0; washCost.calls++ }
}
